import React, { useRef, useState } from 'react';
import { Waves, Wind, ArrowUp, Thermometer, X, Download, Share2, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import html2canvas from 'html2canvas';
import { useTheme } from '../context/ThemeContext';

const TIME_GRADIENTS = {
  morning: 'linear-gradient(135deg, #ff9a56 0%, #ffcd6b 30%, #87ceeb 70%, #6eb5ff 100%)',
  midday: 'linear-gradient(135deg, #0099cc 0%, #00c9ff 30%, #87ceeb 60%, #48d1cc 100%)',
  evening: 'linear-gradient(135deg, #ff6b35 0%, #ff8c42 25%, #d63384 55%, #6f42c1 100%)',
  night: 'linear-gradient(135deg, #0a1628 0%, #1a2744 30%, #1e3a5f 60%, #0d253f 100%)',
};

const VIBE_LABELS = {
  morning: 'Dawn Patrol',
  midday: 'Peak Session',
  evening: 'Golden Hour',
  night: 'Night Sesh',
};

function getVibePhrase(conditions, timeOfDay) {
  const h = conditions?.wave_height_ft || 0;
  const w = conditions?.wind_speed_kph || 0;
  const p = conditions?.swell_period_s || 0;

  if (h >= 8) return 'heavy sets, respect the ocean';
  if (h >= 5 && w < 15 && p >= 10) return 'pumping & clean, get out there';
  if (h >= 5) return 'solid waves rolling through';
  if (h >= 3 && w < 12) return 'fun & glassy, perfect session';
  if (h >= 3) return 'fun waves on tap';
  if (h >= 1.5) return 'mellow vibes, good for a longboard';
  if (h >= 0.5) return 'small but playful';

  const fallback = {
    morning: 'calm seas, beautiful sunrise',
    midday: 'flat but the water is warm',
    evening: 'quiet ocean, stunning sunset',
    night: 'ocean resting, stars above',
  };
  return fallback[timeOfDay] || 'flat but beautiful';
}

export default function VibeCheck({ conditions, spotName, onClose, user }) {
  const { timeOfDay, bgImage, localTime } = useTheme();
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);

  const vibePhrase = getVibePhrase(conditions, timeOfDay);
  const vibeLabel = VIBE_LABELS[timeOfDay] || 'Vibe Check';
  const gradient = TIME_GRADIENTS[timeOfDay] || TIME_GRADIENTS.midday;
  const dateStr = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const waveH = conditions?.wave_height_ft || 0;
  const isNightOrEvening = timeOfDay === 'night' || timeOfDay === 'evening';

  const captureCard = async () => {
    if (!cardRef.current || generating) return null;
    setGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: null,
        logging: false,
      });
      return canvas;
    } catch {
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    const canvas = await captureCard();
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `surfswell-vibe-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handleShare = async () => {
    const canvas = await captureCard();
    if (!canvas) return;
    try {
      const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
      if (navigator.canShare && blob) {
        const file = new File([blob], 'swell-vibe.png', { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `${waveH}' ${conditions?.summary || ''} at ${spotName}`,
            text: `${vibePhrase} — ${vibeLabel} at ${spotName} via SurfSwell.ai`,
            files: [file],
          });
          return;
        }
      }
      // Fallback: copy to clipboard
      if (blob) {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        alert('Copied to clipboard!');
      }
    } catch (e) {
      if (e.name !== 'AbortError') handleDownload();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="vibe-check-overlay"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="relative z-10 w-full max-w-sm"
      >
        {/* Close */}
        <button
          data-testid="vibe-close-btn"
          onClick={onClose}
          className="absolute -top-3 -right-3 z-20 w-8 h-8 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity shadow-lg"
          style={{ backgroundColor: '#000', border: '2px solid rgba(255,255,255,0.2)' }}
        >
          <X className="w-4 h-4" style={{ color: '#fff' }} />
        </button>

        {/* ====== THE CARD (captured by html2canvas) ====== */}
        <div
          ref={cardRef}
          className="rounded-3xl overflow-hidden shadow-2xl"
          style={{ background: gradient }}
          data-testid="vibe-card"
        >
          {/* Background image layer */}
          <div className="relative" style={{ minHeight: 420 }}>
            <img
              src={bgImage}
              alt=""
              crossOrigin="anonymous"
              className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-40"
              onError={(e) => { e.target.style.display = 'none'; }}
            />

            <div className="relative z-10 p-6 flex flex-col" style={{ minHeight: 420 }}>
              {/* Top — vibe label + time */}
              <div className="flex items-center justify-between mb-auto">
                <span
                  className="text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full"
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(8px)',
                    color: '#fff',
                  }}
                >
                  {vibeLabel}
                </span>
                <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  {localTime}
                </span>
              </div>

              {/* Center — hero number + phrase */}
              <div className="my-auto text-center py-6">
                {waveH > 0 ? (
                  <>
                    <p
                      className="font-heading font-black leading-none"
                      style={{ fontSize: 72, color: '#fff', textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}
                    >
                      {waveH}<span style={{ fontSize: 36 }}>'</span>
                    </p>
                    <p
                      className="text-lg font-medium mt-1"
                      style={{ color: 'rgba(255,255,255,0.9)', textShadow: '0 1px 8px rgba(0,0,0,0.3)' }}
                    >
                      {conditions?.summary || ''}
                    </p>
                  </>
                ) : (
                  <p
                    className="font-heading text-4xl font-bold"
                    style={{ color: '#fff', textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}
                  >
                    Flat day
                  </p>
                )}
                <p
                  className="text-sm mt-3 italic"
                  style={{ color: 'rgba(255,255,255,0.75)', textShadow: '0 1px 6px rgba(0,0,0,0.2)' }}
                >
                  {vibePhrase}
                </p>
              </div>

              {/* Bottom — spot + conditions strip + branding */}
              <div className="mt-auto">
                {/* Spot name */}
                <p
                  className="font-heading text-base font-semibold mb-3"
                  style={{ color: '#fff', textShadow: '0 1px 8px rgba(0,0,0,0.2)' }}
                >
                  {spotName || 'Your spot'}
                </p>

                {/* Conditions strip */}
                {conditions && !conditions.error && (
                  <div
                    className="grid grid-cols-3 gap-2 rounded-2xl p-3 mb-4"
                    style={{ background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(8px)' }}
                  >
                    <div className="text-center">
                      <ArrowUp className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color: 'rgba(255,255,255,0.6)' }} />
                      <p className="text-sm font-bold" style={{ color: '#fff' }}>{conditions.swell_period_s}s</p>
                      <p className="text-[9px]" style={{ color: 'rgba(255,255,255,0.5)' }}>{conditions.swell_direction} swell</p>
                    </div>
                    <div className="text-center">
                      <Wind className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color: 'rgba(255,255,255,0.6)' }} />
                      <p className="text-sm font-bold" style={{ color: '#fff' }}>{Math.round(conditions.wind_speed_kph)} kph</p>
                      <p className="text-[9px]" style={{ color: 'rgba(255,255,255,0.5)' }}>{conditions.wind_direction} wind</p>
                    </div>
                    <div className="text-center">
                      <Thermometer className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color: 'rgba(255,255,255,0.6)' }} />
                      <p className="text-sm font-bold" style={{ color: '#fff' }}>{Math.round(conditions.air_temp_f)}&deg;F</p>
                      <p className="text-[9px]" style={{ color: 'rgba(255,255,255,0.5)' }}>air temp</p>
                    </div>
                  </div>
                )}

                {/* Branding strip */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Waves className="w-4 h-4" style={{ color: isNightOrEvening ? '#00e0ff' : '#fff' }} />
                    <span
                      className="text-xs font-bold tracking-wide"
                      style={{ color: isNightOrEvening ? '#00e0ff' : '#fff' }}
                      data-testid="vibe-brand-text"
                    >
                      SurfSwell.ai{user?.referral_code ? `/r/${user.referral_code}` : ''}
                    </span>
                  </div>
                  <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    {dateStr}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ====== ACTION BUTTONS (outside card, not captured) ====== */}
        <div className="flex items-center justify-center gap-3 mt-4">
          <button
            data-testid="vibe-share-btn"
            onClick={handleShare}
            disabled={generating}
            className="flex items-center gap-2 bg-primary text-primary-foreground font-bold text-sm px-6 py-3 rounded-2xl hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            Share
          </button>
          <button
            data-testid="vibe-download-btn"
            onClick={handleDownload}
            disabled={generating}
            className="flex items-center gap-2 text-sm font-medium px-6 py-3 rounded-2xl hover:opacity-80 transition-opacity shadow-md disabled:opacity-50"
            style={{ backgroundColor: '#000', color: '#fff' }}
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Save
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
